/**
 * useIdempotencyKey — dialog-scoped UUIDv4 generator for `Idempotency-Key`
 * headers.
 *
 * Per design.md §8 *Idempotency-key generation* and Q7 / NFR-4:
 *
 *   - Generates a UUIDv4 the first time `current()` is invoked within a
 *     React effect / handler.
 *   - Returns the **same** key on subsequent invocations within the same
 *     dialog instance — this is what makes save retries replay-safe (the
 *     server's idempotency middleware caches the original envelope on the
 *     first hit and returns it byte-exact on the second).
 *   - `regenerate()` forces a new UUIDv4. Required by the zero-imprest
 *     resubmit (Q8): the body has changed (`zeroImprestResolution` was
 *     added), so reusing the original key would 400 with
 *     IDEMPOTENCY_KEY_MISMATCH. Also called by the dialog when the user
 *     clicks Cancel + reopens to ensure a fresh key per dialog session.
 *   - `reset()` clears the cached key without generating a new one — the
 *     next `current()` call will produce a fresh UUIDv4. Used when the
 *     dialog unmounts / closes.
 *
 * Implementation note: a `useRef` carries the key across re-renders so we
 * don't accidentally re-generate on every render. `crypto.randomUUID()` is
 * available in every browser the platform supports (RUM-instrumented
 * Chromium, Firefox, Safari ≥ 15.4).
 */
import { useCallback, useEffect, useRef } from 'react';

export interface UseIdempotencyKeyResult {
  /**
   * Returns the current UUIDv4. Generates one lazily on first call within
   * the current dialog session.
   */
  current(): string;
  /**
   * Forces a fresh UUIDv4 — returns the new value. Called before the
   * zero-imprest resubmit (Q8) and on dialog reopen.
   */
  regenerate(): string;
  /**
   * Drops the cached key without generating a replacement. The next
   * `current()` call lazily generates one. Used on dialog close.
   */
  reset(): void;
}

/**
 * Hook returns a stable handle whose methods always reflect the latest key.
 * The handle's identity is stable across re-renders so consumers can pass
 * it as a dependency in `useCallback` / `useEffect` arrays without forcing
 * a re-run.
 */
export function useIdempotencyKey(): UseIdempotencyKeyResult {
  const keyRef = useRef<string | null>(null);

  const current = useCallback((): string => {
    if (keyRef.current === null) {
      keyRef.current = crypto.randomUUID();
    }
    return keyRef.current;
  }, []);

  const regenerate = useCallback((): string => {
    keyRef.current = crypto.randomUUID();
    return keyRef.current;
  }, []);

  const reset = useCallback((): void => {
    keyRef.current = null;
  }, []);

  // Stable handle — only the captured methods change identity (they don't,
  // they're memoized via useCallback with empty deps). Returning a fresh
  // object on every render is fine because the methods themselves are stable.
  return { current, regenerate, reset };
}

/**
 * useDialogIdempotencyKey — `useIdempotencyKey` scoped to a dialog *session*
 * rather than a component *mount*.
 *
 * Every write dialog rendered through `EditPanel` stays mounted while closed
 * (`open` only toggles the panel's CSS class), so a mount-scoped key —
 * `useMemo(() => crypto.randomUUID(), [])` or a bare `useIdempotencyKey()`
 * with no regenerate call — lives for the whole tool session. The first save
 * succeeds and every later save in the session 400s with
 * IDEMPOTENCY_KEY_MISMATCH because the key was already consumed by a
 * different body (IC-197).
 *
 * This wrapper regenerates the key on every closed→open transition, restoring
 * the per-dialog-session semantics the server contract expects: reuse within
 * a session keeps same-body retries replay-safe; a new session gets a new key.
 */
export function useDialogIdempotencyKey(open: boolean): UseIdempotencyKeyResult {
  const { current, regenerate, reset } = useIdempotencyKey();

  useEffect(() => {
    if (open) regenerate();
  }, [open, regenerate]);

  return { current, regenerate, reset };
}
